import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    // Read from localStorage (set by the blocking script in index.html)
    const saved = localStorage.getItem('theme');
    return (saved && saved !== 'system') ? saved : 'light';
  });

  // Apply theme to <html> and manage transition class
  const applyTheme = useCallback((newTheme) => {
    // Determine the actual color mode
    let resolvedTheme = newTheme;
    if (newTheme === 'system') {
      resolvedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    // Add no-transitions class before changing to prevent flash
    document.documentElement.classList.add('no-transitions');
    
    // Set the attribute
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    
    // Update theme-color meta tag for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', resolvedTheme === 'dark' ? '#0B1121' : '#ffffff');
    }
    
    // Remove no-transitions after a tick to re-enable smooth transitions
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.classList.remove('no-transitions');
      });
    });
  }, []);

  // Set theme and persist
  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
  }, [applyTheme]);

  // Listen for system preference changes (only when in 'system' mode)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      if (theme === 'system') {
        applyTheme('system');
      }
      // Update the cached system state
      localStorage.setItem('system_is_dark', e.matches ? 'true' : 'false');
    };

    // Add listener (modern browsers)
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, applyTheme]);

  // Apply on mount
  useEffect(() => {
    applyTheme(theme);
  }, []);

  // Determine the currently active resolved theme
  const resolvedTheme = theme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;